// Public prospect onboarding page: /onboard/:token - no login, token IS the access.
// Mirrors questionnaire v1.3 (sections 1-17). Autosaves. Mobile-first. No PHI collected.
import React, { useState, useEffect, useRef, useCallback } from 'react';

const API = 'https://tcio-api.onrender.com';
const NAVY = '#12234f', BLUE = '#1f5fbf', BG = '#f4f6fb', BORDER = '#d5dcea', INK = '#1c2333';

const T = (id, label) => ({ k: 'text', id, label });
const TA = (id, label) => ({ k: 'ta', id, label });
const C = (id, label) => ({ k: 'check', id, label });
const H = (label) => ({ k: 'h', label });
const TBL = (id, label, cols, rows) => ({ k: 'tbl', id, label, cols, rows });

const SECTIONS = [
  { id: 's1', title: '1. Organization & Facility', fields: [
    T('legal_entity_name', 'Legal Entity Name'), T('ein_tax_id', 'EIN (Tax ID)'),
    T('facility_name_license', 'Facility Name (as on license)'), T('dba_marketing_name', 'DBA / Marketing Name'),
    T('service_address', 'Service Address'), T('mailing_address', 'Mailing Address'),
    T('city_state_zip', 'City, State, Zip'), T('main_phone', 'Main Phone'), T('website', 'Website'),
    T('state_license_numbers', 'State License Number(s)'), T('license_expiration', 'License Expiration Date(s)'),
    T('licensed_beds', 'Licensed Bed Capacity'), T('npi_numbers', 'NPI Number(s)'),
    T('target_occupancy', 'Target Occupancy %'),
    H('Accreditations'), C('acc_jc', 'Joint Commission'), C('acc_carf', 'CARF'), T('acc_other', 'Other accreditation'),
  ]},
  { id: 's2', title: '2. Services & Houses', fields: [
    H('Levels of care offered'), C('loc_dtx', 'Detox (DTX)'), C('loc_rtc', 'Residential (RTC)'), C('loc_php', 'PHP'),
    C('loc_iop', 'IOP'), C('loc_op', 'Outpatient (OP)'), C('loc_sle', 'Sober Living (SLE)'), T('loc_other', 'Other LOC'),
    H('Billed day-rate per LOC'), T('rate_dtx', 'DTX rate'), T('rate_rtc', 'RTC rate'), T('rate_php', 'PHP rate'),
    T('rate_iop', 'IOP rate'), T('rate_op', 'OP rate'), T('rate_sle', 'SLE rate'),
    TBL('houses', 'Houses / locations', ['House Name', 'Address', 'License #', '# Beds', 'Type (DTX/RTC/...)'], 4),
  ]},
  { id: 's3', title: '3. Key Contacts', fields: [
    TBL('contacts', 'Key contacts', ['Name', 'Title / Role', 'Email', 'Phone', 'Responsibilities'], 4),
  ]},
  { id: 's4', title: '4. Payers & Billing', fields: [
    T('billing_company', 'Billing Company Name'), T('billing_contact', 'Billing Company Contact'),
    T('billing_email', 'Billing Email'), T('billing_phone', 'Billing Phone'),
    TA('payers_list', 'Payers (in-network) - list them here'),
  ]},
  { id: 's5', title: '5. Systems & Vendors', fields: [
    T('current_emr', 'Current EMR Vendor'), T('current_crm', 'Current CRM (if any)'),
    T('call_tracking_vendor', 'Call Tracking Vendor (if any)'), T('lab_vendor', 'Lab Vendor (if any)'),
    TA('other_systems', 'Other key systems / tools'),
  ]},
  { id: 's6', title: '6. Documents You Can Provide', fields: [
    H('Check what you can send (email or upload separately)'),
    C('doc_license', 'DHCS License / Certification'), C('doc_coi', 'Professional Liability COI'),
    C('doc_roster', 'Staff Roster'), C('doc_schedule', 'Program Schedule'),
  ]},
  { id: 's7', title: '7. Billing & Revenue History', fields: [
    T('prev_biller', 'Previous Billing Vendor'), T('prev_biller_contact', 'Primary Contact'),
    T('prev_biller_email', 'Contact Email'), T('prev_biller_phone', 'Phone'),
    H('Revenue-history files you can provide'),
    C('rev_ar_export', 'Weekly open-AR export (CSV)'), C('rev_remit', 'Paid/remittance history (12+ months)'),
    C('rev_deposits', 'Bank deposit history (6-12 months)'),
    T('census_email', 'Current census delivery email'), T('census_day', 'Day of week'), T('census_format', 'Format'),
    TA('billing_notes', 'Billing / revenue notes'),
  ]},
  { id: 's8', title: '8. CRM / Admissions History', fields: [
    T('crm_system', 'Current CRM / Admissions System'),
    H('Available exports'),
    C('crm_contacts_export', 'Historical contacts/opportunities export'), C('crm_lead_sources', 'Lead source list'),
    C('crm_referral_dir', 'Referral partner directory'), C('crm_ad_accounts', 'Ad account info (Google/Meta)'),
    TA('crm_notes', 'CRM / admissions notes'),
  ]},
  { id: 's9', title: '9. Clinical / EMR', fields: [
    T('emr_vendor', 'Current EMR Vendor'), T('emr_export_contact', 'Data Export / API Access Contact'),
    T('mrn_prefix', 'Desired MRN Prefix'), T('mrn_start', 'Starting Sequence'),
    H('Clinical documents & info you can provide'),
    C('cl_packet', 'State-approved intake packet'), C('cl_consents', 'Consent / ROI forms in use'),
    C('cl_note_types', 'Note types & signature/cosign requirements'), C('cl_group_schedule', 'Group therapy schedule'),
    C('cl_rounds', 'Rounds requirements'), C('cl_vitals_mar', 'Vitals protocols & MAR parameters'),
    C('cl_lab_flow', 'Lab vendor & requisition flow'),
    H('Compliance frameworks to enforce'),
    C('cf_dhcs', 'DHCS / State'), C('cf_jc', 'Joint Commission'), C('cf_carf', 'CARF'), T('cf_other', 'Other framework'),
    TA('note_cosign_details', 'Note types / signature-cosign details'),
    TA('rounds_vitals_notes', 'Rounds / vitals / MAR / lab notes'),
  ]},
  { id: 's10', title: '10. Operations', fields: [
    TA('shift_structure', 'Shift structure per house (Day / Swing / NOC times & staffing)'),
    T('wage_state', 'State for wage rules'), T('driver_schedules', 'Driver schedules (if applicable)'),
    T('schedule_owner', 'Who builds the weekly schedule?'),
  ]},
  { id: 's11', title: '11. Finance Admin', fields: [
    H('Bonus program configuration (if used)'),
    T('bonus_floor', 'Floor score'), T('bonus_cap', 'Per-rep cap'), T('bonus_tiers', 'Team tiers'), T('bonus_stretch', 'Stretch pool'),
    T('cash_pay_pricing', 'Cash-pay pricing (e.g., 30-day rate)'),
    T('collections_benchmark', 'Monthly collections benchmark'),
  ]},
  { id: 's12', title: '12. Tech & Access', fields: [
    T('outbound_domain', 'Domain / email identity for outbound'),
    T('spf_dkim', 'SPF / DKIM setup status'), C('baa_signed', 'Signed BAA with TCIO'),
    TA('phi_vendors', 'Other vendors touching PHI (transcription, e-fax, labs, etc.)'),
    T('logo_brand', 'Logo / brand color'), T('owner_admin', 'Facility TCIO Owner-Admin (name/email)'),
  ]},
  { id: 's13', title: '13. Additional Information', fields: [
    TA('additional_info', 'Anything else we should know for a successful onboarding?'),
  ]},
  { id: 's14', title: '14. Staff Roster for TCIO Logins', fields: [
    H('Roles: Owner-Admin, Clinical, Nursing, Admissions, UR, Billing, Operations'),
    TBL('staff', 'Everyone who needs a TCIO login', ['Name', 'Email', 'Title', 'TCIO Role'], 6),
  ]},
  { id: 's15', title: '15. Rooms & Beds per House', fields: [
    H('The bed board is built bed-by-bed (e.g., Room 2 / Bed A)'),
    TBL('beds', 'Rooms & beds', ['House', 'Room', 'Bed Label', 'Gender (M/F/Any)'], 8),
  ]},
  { id: 's16', title: '16. Census at Cutover', fields: [
    H('Do NOT enter client names or details here. The census moves through a secure channel at kickoff.'),
    T('census_count', 'Number of clients currently in care'), T('monthly_admits', 'Typical monthly admissions'),
    C('secure_ack', 'We understand the detailed census is provided only via the secure method TCIO specifies at kickoff.'),
  ]},
  { id: 's17', title: '17. Go-Live, Phones & Time Zone', fields: [
    T('target_golive', 'Target Go-Live date'), T('timezone', 'Facility time zone'),
    T('admissions_number', 'Main admissions number'), T('afterhours_contact', 'After-hours technical contact'),
    T('tracking_numbers', 'Existing tracking numbers (list all)'),
    TA('routing_notes', 'Where should inbound admissions calls route (person/team/hours)?'),
  ]},
];

const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: 16,
  border: '1px solid ' + BORDER, borderRadius: 8, background: '#fff', color: INK, outline: 'none' };

export default function OnboardingPage({ token }) {
  const [state, setState] = useState('loading');
  const [meta, setMeta] = useState({});
  const [answers, setAnswers] = useState({});
  const [section, setSection] = useState('s1');
  const [saveState, setSaveState] = useState('idle');
  const [errMsg, setErrMsg] = useState('');
  const dirtyRef = useRef({});
  const timerRef = useRef(null);

  useEffect(() => {
    fetch(API + '/api/onboard/' + encodeURIComponent(token))
      .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Could not load'); return d; })
      .then(d => {
        setMeta(d);
        setAnswers(d.answers || {});
        if (d.submitted_at) setState('submitted');
        else { setState('form'); if (d.last_section) setSection(d.last_section); }
      })
      .catch(e => { setErrMsg(e.message); setState('error'); });
  }, [token]);

  const flush = useCallback(() => {
    const payload = dirtyRef.current;
    if (!Object.keys(payload).length) return;
    dirtyRef.current = {};
    setSaveState('saving');
    fetch(API + '/api/onboard/' + encodeURIComponent(token), {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ answers: payload, last_section: section })
    }).then(r => setSaveState(r.ok ? 'saved' : 'failed'))
      .catch(() => setSaveState('failed'));
  }, [token, section]);

  const setField = (id, val) => {
    setAnswers(a => ({ ...a, [id]: val }));
    dirtyRef.current[id] = val;
    setSaveState('saving');
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, 900);
  };

  const submit = () => {
    flush();
    setTimeout(() => {
      fetch(API + '/api/onboard/' + encodeURIComponent(token) + '/submit', { method: 'POST' })
        .then(async r => { const d = await r.json(); if (!r.ok) throw new Error(d.error || 'Submit failed'); setState('submitted'); })
        .catch(e => alert(e.message));
    }, 400);
  };

  if (state === 'loading') return <Center><div>Loading your onboarding form...</div></Center>;
  if (state === 'error') return <Center><Card><h2 style={{ color: NAVY }}>TCIO Onboarding</h2><p>{errMsg}</p></Card></Center>;
  if (state === 'submitted') return (
    <Center><Card>
      <h2 style={{ color: NAVY, marginTop: 0 }}>Thank you{meta.prospect_name ? ', ' + meta.prospect_name.split(' ')[0] : ''}!</h2>
      <p>Your onboarding questionnaire has been submitted to the TCIO team. We will review it and follow up shortly to schedule your kickoff.</p>
      <p style={{ color: '#667', fontSize: 14 }}>Need to change something? Contact your TCIO representative.</p>
    </Card></Center>
  );

  const sec = SECTIONS.find(s => s.id === section) || SECTIONS[0];
  const idx = SECTIONS.findIndex(s => s.id === sec.id);

  return (
    <div style={{ minHeight: '100vh', background: BG, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ background: NAVY, color: '#fff', padding: '14px 16px', position: 'sticky', top: 0, zIndex: 10 }}>
        <div style={{ maxWidth: 760, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 17, letterSpacing: 0.3 }}>TCIO Onboarding</div>
            <div style={{ fontSize: 12.5, opacity: 0.85 }}>{meta.company_name || meta.prospect_name || ''}</div>
          </div>
          <div style={{ fontSize: 12, opacity: 0.9 }}>
            {saveState === 'saving' ? 'Saving...' : saveState === 'saved' ? 'Saved' : saveState === 'failed' ? 'Save failed - will retry on next edit' : ''}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 760, margin: '0 auto', padding: '10px 12px 90px' }}>
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', padding: '8px 2px', WebkitOverflowScrolling: 'touch' }}>
          {SECTIONS.map(s => (
            <button key={s.id} onClick={() => { flush(); setSection(s.id); window.scrollTo(0, 0); }}
              style={{ flex: '0 0 auto', padding: '7px 11px', borderRadius: 16, fontSize: 12.5, cursor: 'pointer',
                border: '1px solid ' + (s.id === sec.id ? NAVY : BORDER),
                background: s.id === sec.id ? NAVY : '#fff', color: s.id === sec.id ? '#fff' : INK, fontWeight: 600 }}>
              {s.title.split('.')[0]}
            </button>
          ))}
        </div>

        <Card>
          <h3 style={{ color: NAVY, margin: '2px 0 14px' }}>{sec.title}</h3>
          {sec.fields.map((f, i) => <Field key={f.id || 'h' + i} f={f} answers={answers} setField={setField} />)}
        </Card>

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14, gap: 10 }}>
          <button disabled={idx === 0} onClick={() => { flush(); setSection(SECTIONS[idx - 1].id); window.scrollTo(0, 0); }}
            style={navBtn(idx === 0)}>Back</button>
          {idx < SECTIONS.length - 1 ? (
            <button onClick={() => { flush(); setSection(SECTIONS[idx + 1].id); window.scrollTo(0, 0); }}
              style={{ ...navBtn(false), background: NAVY, color: '#fff', border: '1px solid ' + NAVY }}>Next</button>
          ) : (
            <button onClick={submit}
              style={{ ...navBtn(false), background: '#1a7f37', color: '#fff', border: '1px solid #1a7f37', fontWeight: 700 }}>
              Submit to TCIO</button>
          )}
        </div>
        <p style={{ textAlign: 'center', color: '#78839a', fontSize: 12, marginTop: 18 }}>
          Your answers save automatically - you can close this page and return using the same link.<br />
          Please do not enter any client/patient information in this form.
        </p>
      </div>
    </div>
  );
}

function Center({ children }) {
  return <div style={{ minHeight: '100vh', background: BG, display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', padding: 16 }}>{children}</div>;
}
function Card({ children }) {
  return <div style={{ background: '#fff', border: '1px solid ' + BORDER, borderRadius: 12, padding: '18px 16px',
    boxShadow: '0 1px 3px rgba(18,35,79,0.06)', maxWidth: 760 }}>{children}</div>;
}
const navBtn = (disabled) => ({ padding: '12px 22px', borderRadius: 10, fontSize: 15, cursor: disabled ? 'default' : 'pointer',
  border: '1px solid ' + BORDER, background: '#fff', color: disabled ? '#aab' : INK, opacity: disabled ? 0.5 : 1 });

function Field({ f, answers, setField }) {
  if (f.k === 'h') return <div style={{ fontWeight: 700, fontSize: 13.5, color: BLUE, margin: '14px 0 8px' }}>{f.label}</div>;
  if (f.k === 'text') return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: INK, marginBottom: 4 }}>{f.label}</div>
      <input style={inputStyle} value={answers[f.id] || ''} onChange={e => setField(f.id, e.target.value)} />
    </label>
  );
  if (f.k === 'ta') return (
    <label style={{ display: 'block', marginBottom: 12 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: INK, marginBottom: 4 }}>{f.label}</div>
      <textarea rows={3} style={{ ...inputStyle, resize: 'vertical' }} value={answers[f.id] || ''} onChange={e => setField(f.id, e.target.value)} />
    </label>
  );
  if (f.k === 'check') return (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10, cursor: 'pointer' }}>
      <input type="checkbox" checked={!!answers[f.id]} onChange={e => setField(f.id, e.target.checked)}
        style={{ width: 20, height: 20, marginTop: 1 }} />
      <span style={{ fontSize: 14.5, color: INK }}>{f.label}</span>
    </label>
  );
  if (f.k === 'tbl') return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: INK, marginBottom: 6 }}>{f.label}</div>
      {Array.from({ length: f.rows }, (_, r) => (
        <div key={r} style={{ border: '1px solid ' + BORDER, borderRadius: 10, padding: 10, marginBottom: 8, background: r % 2 ? '#fafbfe' : '#fff' }}>
          <div style={{ fontSize: 11.5, color: '#8a93a8', fontWeight: 700, marginBottom: 6 }}>{'#' + (r + 1)}</div>
          {f.cols.map((c, ci) => (
            <input key={ci} placeholder={c} style={{ ...inputStyle, marginBottom: 6 }}
              value={answers[f.id + '_' + (r + 1) + '_' + (ci + 1)] || ''}
              onChange={e => setField(f.id + '_' + (r + 1) + '_' + (ci + 1), e.target.value)} />
          ))}
        </div>
      ))}
    </div>
  );
  return null;
}
